/* =========================================================================
   Recipe parsing.

   Two engines:

   - parseRecipeRemote()  Claude, via the Cloudflare Function. Handles URLs
                          (fetched server-side, past CORS), messy captions and
                          article prose, and photos/screenshots via vision.
   - parseRecipeText()    The original regex heuristics from iteration 1. Kept
                          as the offline fallback and for when the API key
                          isn't configured. Classifies lines by shape: starts
                          with a quantity? contains a unit? starts with a verb?

   The UI tries remote first and falls back to local automatically, so the app
   keeps working offline and on a deployment with no key attached.
   ========================================================================= */

import { toBase64Jpeg } from './images.js'

const ENDPOINT = '/api/parse-recipe'

/** Is the input just a bare URL rather than recipe text? */
export function looksLikeBareUrl(s) {
  const t = s.trim()
  return /^https?:\/\/\S+$/i.test(t) && !/\s/.test(t)
}

/**
 * Ask the server to parse. Throws on any failure so the caller can fall back.
 *
 * @param {{text?:string, url?:string, image?:File|Blob}} input
 * @returns {Promise<{via:string, recipe:object}>}
 */
export async function parseRecipeRemote(input) {
  const body = {}
  if (input.url) body.url = input.url
  if (input.text) body.text = input.text
  if (input.image) {
    const { base64, mediaType } = await toBase64Jpeg(input.image)
    body.imageBase64 = base64
    body.imageMediaType = mediaType
  }

  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })

  // A static host with no Functions returns the HTML shell instead of JSON.
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) {
    throw Object.assign(new Error('no-endpoint'), { code: 'no-endpoint' })
  }

  const data = await res.json()
  if (!res.ok) {
    throw Object.assign(new Error(data.error || `HTTP ${res.status}`), {
      code: 'server-error',
      detail: data.detail,
    })
  }
  return data
}

/** Is the AI endpoint deployed and holding a key? */
export async function checkEndpoint() {
  try {
    const res = await fetch(ENDPOINT)
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) return { available: false, configured: false }
    const d = await res.json()
    return { available: !!d.ok, configured: !!d.configured, model: d.model }
  } catch {
    return { available: false, configured: false }
  }
}

const JUNK_RE = /^(save|saved|print|share|shared|advertisement|ad|sponsored|view in browser|read in app|subscribe[d]?|sign in|log ?in|comments?|reply|like[sd]?|follow|photo(graph)?y?(\s+by)?.*|recipe by.*|also try:?.*|©.*|all rights reserved.*|easy|medium|hard|difficulty.*|(prep|cook|total|resting|chilling)\s*time.*|time:.*|makes:?\s*\d+.*|serves?:?\s*\d+.*|\d+\s*(comments?|likes?|notes?|recipe notes?|min(ute)?s?)|\d*\s*recipe notes?|∙?\s*paid|free|updated.*|published.*|jump to recipe|print recipe|pin (this|recipe).*|\d+|[•\-*–—]+)$/i
const JUNKY_INLINE_RE = /time:\s*<?\s*\d+|serves\s*:\s*\d+\s*$/i
const SUBHEAD_RE = /^(for\s+the\s+|the\s+)?(dressing|sauce|marinade|topping|garnish|salad(\s+base)?|base|to\s+serve|to\s+finish|filling|pastry|glaze|pickles?|salsa|dip|croutons?|crumb|crust|spice\s+mix|paste|dough|batter|icing|cream|yoghurt|custard)\s*:?$/i
const VERB_RE = /^(pre|heat|preheat|warm|toss|blanch|mix|combine|add|pour|place|put|bake|roast|grill|fry|sear|boil|simmer|steam|serve|drain|rinse|season|spoon|stir|whisk|fold|cook|remove|cool|chill|refrigerate|slice|chop|dice|crush|grate|zest|juice|peel|trim|halve|quarter|tear|scatter|top|finish|brush|drizzle|garnish|taste|rest|set|transfer|arrange|line|grease|cover|wrap|reduce|deglaze|skim|carve|shred|mash|blend|process|pulse|assemble|repeat|meanwhile|start|begin|when|once|just before|using|for the)\b/i
const UNIT_RE = new RegExp('\\b(' + ['g', 'kg', 'mg', 'ml', 'l', 'cup', 'cups', 'tbsp', 'tsp', 'tablespoons?', 'teaspoons?', 'oz', 'lb', 'cans?', 'tins?', 'cloves?', 'sprigs?', 'bunch(es)?', 'slices?', 'heads?', 'sticks?', 'pinch(es)?', 'handfuls?', 'rashers?', 'punnets?', 'litres?', 'bulbs?'].join('|') + ')\\b', 'i')
const ING_HINT_RE = /(to\s+(serve|taste|garnish|finish)|,\s*(chopped|sliced|diced|crushed|grated|torn|minced|peeled|trimmed|halved|quartered|drained|rinsed|softened|melted|zested|juiced|finely|roughly|thinly))/i
const BULLET_RE = /^[•·‣◦▪\-*–—]\s*/
const INLINE_NEED_RE = /^(?:you(?:'ll)?\s+(?:will\s+)?need|ingredients?)\s*[:\-]\s*\S/i

function looksLikeIngredient(l) {
  if (INLINE_NEED_RE.test(l)) return true
  if (/^[•·‣◦▪\-*–—]?\s*(\d|[¼½¾⅓⅔⅛])/.test(l) && l.length < 100) return true
  if (BULLET_RE.test(l) && l.length < 100) return true
  if (l.length < 70 && (UNIT_RE.test(l) || ING_HINT_RE.test(l))) return true
  return false
}

function looksLikeLooseIngredient(l) {
  return l.length < 70 && !/[.!?]$/.test(l) && !VERB_RE.test(l)
}

function looksLikeMethod(l) {
  return l.length > 25 && VERB_RE.test(l)
}

function tidyTitle(t) {
  t = t.replace(/^#+\s*/, '').replace(/^(recipe|recipe:)\s*/i, '').trim()
  t = t.split(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u)[0]
  t = t.split(/\s+[–—|]\s+/)[0]
  t = t.replace(/[\s:,\-–—]+$/, '').slice(0, 110).trim()
  if (t && t === t.toUpperCase() && /[A-Z]{4}/.test(t)) {
    t = t.toLowerCase().replace(/(^|\s|\()([a-z])/g, (m, a, b) => a + b.toUpperCase())
  }
  return t
}

/**
 * @returns {{title:string, url:string, serves:number|null,
 *            ingredients:string[], method:string[]}}
 */
export function parseRecipeText(raw) {
  const lines = raw.split(/\r?\n/).map(l => l.replace(/\s+/g, ' ').trim())

  const urlM = raw.match(/https?:\/\/[^\s"'<>)]+/)
  const url = urlM ? urlM[0].replace(/[.,)]+$/, '') : ''

  const servesM = raw.match(/serves?\s*[:\-]?\s*(\d+)/i)
    || raw.match(/for\s+(\d+)\s+people/i)
    || raw.match(/makes\s*[:\-]?\s*(\d+)/i)
  const serves = servesM ? parseInt(servesM[1], 10) : null

  const isIngHdr = l => /^ingredients?\b\s*:?$/i.test(l) || /^ingredients?\s*:/i.test(l)
  const isMethHdr = l => /^(method|instructions?|directions?|steps?|preparation|to\s+make)\b\s*:?$/i.test(l)

  let ingStart = -1, methStart = -1
  lines.forEach((l, i) => {
    if (ingStart < 0 && isIngHdr(l)) ingStart = i
    if (methStart < 0 && i > ingStart && isMethHdr(l)) methStart = i
  })

  let ingRaw = [], methRaw = []
  if (ingStart >= 0) {
    const end = methStart > ingStart ? methStart : lines.length
    ingRaw = lines.slice(ingStart + 1, end)
    if (methStart >= 0) methRaw = lines.slice(methStart + 1)
  } else {
    let inIngRun = false
    for (const l of lines.slice(1)) {
      if (!l || JUNK_RE.test(l) || JUNKY_INLINE_RE.test(l) || /^https?:/.test(l)) continue
      if (looksLikeIngredient(l)) { ingRaw.push(l); inIngRun = true }
      else if (looksLikeMethod(l)) { methRaw.push(l); inIngRun = false }
      else if (inIngRun && looksLikeLooseIngredient(l)) ingRaw.push(l)
    }
  }

  const ings = []
  let section = ''
  for (let l of ingRaw) {
    if (!l || /^https?:/.test(l)) continue
    l = l.replace(BULLET_RE, '').trim()
    if (!l || JUNK_RE.test(l) || JUNKY_INLINE_RE.test(l)) continue
    const inline = l.match(/^(?:you(?:'ll)?\s+(?:will\s+)?need|ingredients?)\s*[:\-]\s*(.+)$/i)
    if (inline) {
      inline[1].split(/,\s*(?![^(]*\))/).forEach(p => {
        p = p.replace(/^and\s+/i, '').trim()
        if (p) ings.push(p)
      })
      continue
    }
    const looksSection = !/\d/.test(l) && !ING_HINT_RE.test(l) && l.split(/\s+/).length <= 6 &&
      (/^(for\s+the|the)\s+/i.test(l) || /:$/.test(l))
    if (SUBHEAD_RE.test(l) || looksSection) {
      section = l.replace(/:$/, '').replace(/^(for\s+the|the)\s+/i, '').trim().toLowerCase()
      continue
    }
    ings.push(section ? l + ' (' + section + ')' : l)
  }

  const steps = []
  for (let l of methRaw) {
    if (!l || /^https?:/.test(l)) continue
    if (/^step\s*\d+\s*[:.)]?$/i.test(l)) continue
    l = l.replace(/^step\s*\d+\s*[:.)]?\s*/i, '').replace(/^\d+\s*[.)]\s*/, '').replace(BULLET_RE, '').trim()
    if (!l || JUNK_RE.test(l) || JUNKY_INLINE_RE.test(l)) continue
    if (/^also try/i.test(l)) continue
    steps.push(l)
  }

  if (!ings.length) {
    const cand = lines
      .filter(l => (l.match(/,/g) || []).length >= 2 && (UNIT_RE.test(l) || /\d/.test(l)))
      .sort((a, b) => b.length - a.length)[0]
    if (cand) {
      cand.replace(/^[^:]*:\s*/, '').split(/,\s*/).forEach(p => {
        p = p.replace(/^and\s+/i, '').trim()
        if (p) ings.push(p)
      })
    }
  }

  const titleLine = lines.find(l => l && !/^https?:/.test(l) && !JUNK_RE.test(l)) || 'New recipe'

  return {
    title: tidyTitle(titleLine) || 'New recipe',
    url,
    serves,
    ingredients: ings,
    method: steps,
  }
}
